/**
 * The arrange step's "chia ngày cho tôi": a defensible starting distribution of places over days.
 *
 * The action exists because a flat list of thirty saved places with no days is not an itinerary, and
 * asking the user to empty it into day columns by hand is the work they came here to avoid. What they get
 * instead is a split they can then move things around in.
 *
 * **It is a heuristic, and the screen says so out loud.** The order the days come out in is a starting
 * point the user can change, so the value of this module is not that the answer is optimal — no such thing
 * is computable from coordinates alone — but that it is deterministic and explainable: the same trip
 * splits the same way twice, and every choice a day made can be described in a sentence. That is the
 * promise the tests hold it to.
 *
 * Two facts are counted rather than hidden, like everywhere else in this step. A place saved without
 * coordinates (AC-2.5) cannot be placed on a map or routed, but it is still a place the user chose, so it
 * is spread over the days rather than dropped and the screen can say how many of a day's stops it cannot
 * draw. And a trip with no anchor at all falls back to splitting in input order — deterministic, and
 * honestly arbitrary instead of pretending to be geographic.
 */

import { haversineMetres, type LatLon } from "@trippool/core";

/** A place as the distribution sees it: an id, and a coordinate or the honest absence of one. */
export interface AutoArrangePlace {
  id: string;
  lat: number | null;
  lon: number | null;
}

export interface AutoArrangeInput {
  /** Every place the trip holds, in the order the user saved them. */
  places: readonly AutoArrangePlace[];
  /** How many days to split them over. Fewer than one is not a trip and yields no days. */
  days: number;
  /**
   * Where a day starts, when the trip knows — the lodging, normally.
   *
   * A function rather than an array because a day's anchor is not always known: a day whose stay is not
   * saved yet, or a day the user has not touched, answers null. The distribution reads the anchor three
   * times — to decide which day a place is nearest, to balance the days against each other, and to start
   * each day's walk — and a null anywhere simply means that day has no home to measure from.
   */
  anchorOf?: (day: number) => { lat: number; lon: number } | null;
}

/** A place the distribution can measure, kept apart from the id alone so the arithmetic stays total. */
interface LocatedPlace {
  id: string;
  lat: number;
  lon: number;
}

/**
 * The split, keyed by a 1-based day number whose value is that day's place ids in visiting order.
 *
 * Every day from 1 to `days` is present, empty when nothing landed on it, because the screen draws a
 * column per day and a missing key would draw nothing rather than an empty day the user can drop onto.
 */
export function autoArrangeDays(input: AutoArrangeInput): Record<number, string[]> {
  const days = Math.floor(input.days);
  const byDay: Record<number, string[]> = {};

  if (days < 1) {
    return byDay;
  }

  const located: LocatedPlace[] = [];
  const unlocated: AutoArrangePlace[] = [];

  for (const place of input.places) {
    if (place.lat === null || place.lon === null) {
      unlocated.push(place);
    } else {
      located.push({ id: place.id, lat: place.lat, lon: place.lon });
    }
  }

  const anchorOf = input.anchorOf;
  const anchors: Array<{ lat: number; lon: number } | null> = [];
  for (let day = 1; day <= days; day += 1) {
    anchors.push(anchorOf ? anchorOf(day) : null);
  }
  const hasAnchor = anchors.some((anchor) => anchor !== null);

  // The coordinate places, per day, in the order they arrived. The balance step below rewrites this before
  // anything is ordered.
  const measured: LocatedPlace[][] = [];
  const loose: AutoArrangePlace[][] = [];
  for (let day = 0; day < days; day += 1) {
    measured.push([]);
    loose.push([]);
  }

  if (hasAnchor) {
    // Nearest anchor wins, and the strict comparison is what breaks a tie towards the lower day number: a
    // later day has to be measurably nearer before it takes the place off the earlier one.
    for (const place of located) {
      let bestDay = 0;
      let bestMetres = Number.POSITIVE_INFINITY;

      for (let index = 0; index < days; index += 1) {
        const anchor = anchors[index]!;
        if (anchor === null) {
          continue;
        }

        const metres = haversineMetres([place.lat, place.lon], [anchor.lat, anchor.lon]);
        if (metres < bestMetres) {
          bestMetres = metres;
          bestDay = index;
        }
      }

      measured[bestDay]!.push(place);
    }
  } else {
    // No anchor is known anywhere, so there is no geography to be honest about. Input order round-robin is
    // deterministic, and is at least the order the user saved things in.
    located.forEach((place, index) => measured[index % days]!.push(place));
  }

  // The cap keeps the days comparable even when the geography does not: three anchors in one city and none
  // anywhere else would otherwise put the whole trip on one day. The loop moves the most remote place off
  // the first day over the cap onto the emptiest day, and each move strictly reduces how far over the cap
  // the days are, so it terminates in at most one move per coordinate place — the bound is belt and braces
  // against a future edit to the tie-break, not a reachable case today.
  const cap = located.length === 0 ? 0 : Math.ceil(located.length / days);

  for (let guard = 0; guard <= located.length; guard += 1) {
    let crowded = -1;
    for (let index = 0; index < days; index += 1) {
      if (measured[index]!.length > cap) {
        crowded = index;
        break;
      }
    }

    if (crowded === -1) {
      break;
    }

    let emptiest = 0;
    for (let index = 1; index < days; index += 1) {
      if (measured[index]!.length < measured[emptiest]!.length) {
        emptiest = index;
      }
    }

    const from = measured[crowded]!;
    const anchor = anchors[crowded]!;

    let leaving = from.length - 1;
    if (anchor !== null) {
      let farthest = -1;
      for (let index = 0; index < from.length; index += 1) {
        const place = from[index]!;
        const metres = haversineMetres([place.lat, place.lon], [anchor.lat, anchor.lon]);
        if (metres > farthest) {
          farthest = metres;
          leaving = index;
        }
      }
    }

    const [moved] = from.splice(leaving, 1);
    measured[emptiest]!.push(moved!);
  }

  // A place with no coordinate keeps its round-robin day and goes on the end of it: it cannot be walked to,
  // so it cannot take part in the order, but it is still on the day.
  unlocated.forEach((place, index) => loose[index % days]!.push(place));

  for (let day = 1; day <= days; day += 1) {
    const ordered = walkFrom(measured[day - 1]!, anchors[day - 1]!);
    byDay[day] = [...ordered.map((place) => place.id), ...loose[day - 1]!.map((place) => place.id)];
  }

  return byDay;
}

/**
 * One day's measured places in the order they should be walked.
 *
 * A nearest-neighbour walk: stand at the day's anchor, or at the day's first place when the day has no
 * anchor, and each step take the nearest place not yet visited. The strict comparison is again the tie
 * break — two places the same distance away come out in the order the user saved them, because the first
 * one seen wins and the candidates are held in that order.
 *
 * This is the same greedy choice the balance step makes, and it is greedy for the same reason: a true
 * shortest route needs the road network and is the router's question, and the screen's text already says
 * this order is a starting point. What matters here is that two people looking at the day could retrace
 * why it is in this order.
 */
function walkFrom(places: readonly LocatedPlace[], anchor: { lat: number; lon: number } | null): LocatedPlace[] {
  if (places.length < 2) {
    return [...places];
  }

  const remaining = [...places];
  const ordered: LocatedPlace[] = [];
  let current: LatLon;

  if (anchor === null) {
    const first = remaining.shift()!;
    ordered.push(first);
    current = [first.lat, first.lon];
  } else {
    current = [anchor.lat, anchor.lon];
  }

  while (remaining.length > 0) {
    let nearestIndex = 0;
    let nearestMetres = Number.POSITIVE_INFINITY;

    for (let index = 0; index < remaining.length; index += 1) {
      const place = remaining[index]!;
      const metres = haversineMetres(current, [place.lat, place.lon]);
      if (metres < nearestMetres) {
        nearestMetres = metres;
        nearestIndex = index;
      }
    }

    const [next] = remaining.splice(nearestIndex, 1);
    ordered.push(next!);
    current = [next!.lat, next!.lon];
  }

  return ordered;
}
